import { configureStore, createSelector } from "@reduxjs/toolkit"
import { useMemo } from "react"
import {
  type TypedUseSelectorHook,
  shallowEqual,
  useDispatch,
  useSelector,
} from "react-redux"

import type { List } from "@/api/lists"
import { hydrateQueriesFromViewData } from "@/queries/query-hydration"
import { listsAdapter, Lists as listsReducer } from "@/redux/lists"
import { normalizeWorkspaces, partialInitSchema } from "@/redux/schema"
import { viewManager } from "@/util/viewManager"

export const store = configureStore({
  reducer: {
    lists: listsReducer,
  },
})

// Put this in a function so that we can also use it when
// re-initializing data when embedding OneSchema.
export function initializeStoreFromViewData() {
  // Seed the Lists slice from the viewData workspaces payload (lists are nested
  // in each workspace). Workspaces and templates themselves are read from React
  // Query, seeded by `hydrateQueriesFromViewData` below.
  const normalizedWorkspaces = normalizeWorkspaces(
    viewManager.getOnceThenReadFromStore("workspaces") || [],
  )
  store.dispatch(partialInitSchema({ lists: normalizedWorkspaces.entities.lists }))
}

initializeStoreFromViewData()

// Seed the React Query cache from the bootstrap viewData on the dashboard/app
// init path (embedded launchers hydrate separately after their session init).
// Non-slim renders populate the cache with zero fetches; slim renders skip the
// deferred keys here and lazy-load them via `useDeferredViewData`.
hydrateQueriesFromViewData(viewManager.getViewData())

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

/*************
 * SELECTORS *
 *************/

// Redux Toolkit's EntityAdapter unfortunately doesn't come with a
// selector to convert an array of ids into an array of objects.
// This type of selector is more complicated because it needs to use
// caching/memoization in order to avoid re-renders, so we define
// this function to help create that type of selector given a selector
// that returns the .entities field of the EntityAdapter.
function createUseByIdsSelector<T>(
  entitiesSelector: (state: RootState) => { [id: number]: T },
): (ids: number[]) => T[] {
  // When selecting Entities by ids, we need to create a new selector
  // for each component to handle basic caching per-component.
  //
  // https://react-redux.js.org/api/hooks#using-memoizing-selectors
  const createByIdsSelector = (selectorIds: number[]) =>
    createSelector([entitiesSelector], (entities: { [id: number]: T }) =>
      selectorIds.map((id) => entities[id]),
    )

  return (ids: number[]): T[] => {
    // We'll use useMemo so we get the same selector function back while
    // the ids array is the same.
    const selector = useMemo(() => createByIdsSelector(ids), [ids])

    // Then use shallowEqual here to prevent excessive re-renders.
    return useSelector(selector, shallowEqual)
  }
}

/**************
 * WORKSPACES *
 **************/

// Workspace reads come from React Query (seeded from viewData by
// `hydrateQueriesFromViewData`, or the deferred fetcher when slimmed). These
// hooks are re-exported so consumers keep the same import path and hook
// signatures. The `lists` nested in each workspace are seeded into the Redux
// `lists` slice above and read through the LISTS selectors below.
export {
  useWorkspaceById,
  useAllWorkspacesOfProductType,
  useAllWorkspacesWorkspaces,
  useAllFileFeedsWorkspaces,
  useAllWorkflowsWorkspaces,
  useOrgDefaultWorkspaceWorkspace,
  useUserDefaultWorkspaceWorkspace,
} from "@/queries/workspaces"

/*********
 * LISTS *
 *********/

export const useListsByIds = createUseByIdsSelector(
  (state: RootState) => state.lists.entities,
)

const selectLists = (state: RootState) => state.lists
const listSelectors = listsAdapter.getSelectors(selectLists)

export const useAllLists = () => useAppSelector(listSelectors.selectAll)
export const useLists = () => useAppSelector(listSelectors.selectEntities)
export const useListById = (id?: number): List | undefined =>
  useAppSelector((state: RootState) =>
    id !== undefined ? listSelectors.selectById(state, id) : undefined,
  )

export const useWorkspaceLists = (workspaceId: number): List[] | undefined => {
  const allLists = useAllLists()
  return useMemo(
    () => allLists.filter((list) => list && list.workspaceId === workspaceId),
    [allLists, workspaceId],
  )
}

/*********************************
 * TEMPLATES & TARGET ATTRIBUTES *
 *********************************/

// Templates and target attributes come from React Query (seeded from viewData
// by `hydrateQueriesFromViewData`, or the deferred fetcher when slimmed). These
// hooks are re-exported so consumers keep the same import path and hook
// signatures while the underlying server-state store is React Query rather than
// Redux. The `lists` slice still cascades on template/target-attribute
// deletions (see the mutation call sites).
export {
  useAllTemplates,
  useTemplates,
  useTemplateById,
  useTargetAttributes,
  useTargetAttributesByIds,
  useTemplateAndTargetAttributesById,
} from "@/queries/templates"

/****************
 * ENVIRONMENTS *
 ****************/

// Environments now live in React Query (seeded from viewData by
// `hydrateQueriesFromViewData`). These selectors are re-exported so the ~28
// existing consumers keep the same import path and hook signatures while the
// underlying server-state store is React Query rather than Redux.
export {
  useAllEnvironments,
  useEnvironmentById,
  useProdEnvironment,
  useSandboxEnvironment,
  useDevelopmentEnvironment,
  useNonProdEnvironments,
} from "@/queries/environment"
